import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, where, or } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import StoryCard from './StoryCard';
import StoryThumbnail from './StoryThumbnail';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import CreateAccountCard from './CreateAccountCard';

function MyStories() {
  const [stories, setStories] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStory, setSelectedStory] = useState(null);
  const { user } = useAuth();
  const location = useLocation();
  const [pendingStory, setPendingStory] = useState(location.state?.pendingStory || null);
  const [error, setError] = useState(null);
  const [sessionId] = useState(() => localStorage.getItem('plottie_session_id'));

  useEffect(() => {
    let unsubscribe;

    async function fetchStories() {
      try {
        const storiesRef = collection(db, 'stories');
        let q;

        if (user) {
          // If user is authenticated, query by both userId and sessionId
          q = query(
            storiesRef,
            or(
              where('userId', '==', user.uid),
              where('sessionId', '==', sessionId)
            ),
            orderBy('createdAt', 'desc')
          );
        } else {
          // If not authenticated, query only by sessionId
          q = query(
            storiesRef,
            where('sessionId', '==', sessionId),
            orderBy('createdAt', 'desc')
          );
        }
        
        unsubscribe = onSnapshot(q, (snapshot) => {
          const storiesData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate()
          }));
          
          setStories(storiesData);
          
          if (pendingStory) {
            const newestStory = storiesData[0];
            if (newestStory?.createdAt > pendingStory.startedAt) {
              setPendingStory(null);
            }
          }
        });
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching stories:', error);
        setLoading(false);
      }
    }

    if (sessionId || user) {
      fetchStories();
    }
    return () => unsubscribe?.();
  }, [user, sessionId, pendingStory]);

  useEffect(() => {
    if (pendingStory) {
      // Set a timeout for 10 minutes
      const timeout = setTimeout(() => {
        setPendingStory(null);
        setError('Story generation is taking longer than expected. Please try again.');
      }, 600000); // 10 minutes

      return () => clearTimeout(timeout);
    }
  }, [pendingStory]);

  // Add this useEffect to handle body scroll
  useEffect(() => {
    if (selectedStory) {
      // Disable scrolling on body when story is selected
      document.body.style.overflow = 'hidden';
    } else {
      // Re-enable scrolling when story is closed
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to ensure scrolling is re-enabled when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedStory]);

  if (loading || stories === null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl"></div>
      </div>
    );
  }

  // Add this container variant for the stagger effect
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  // Add this item variant for individual thumbnails
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 pt-24 lg:pt-32 min-h-screen flex flex-col">
      <AnimatePresence>
        {!loading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.6 }}
            className="flex-1"
          >
            {(stories.length > 0 && !pendingStory) && (
              <h1 className="text-[#343434] text-[2.75rem] md:text-[4rem] leading-[110%] tracking-[-0.06375rem] md:tracking-[-0.135rem] mb-2 md:mb-4 text-left font-freeman">
                Meine Geschichten
              </h1>
            )}
            
            <motion.div 
              className="space-y-4 mb-8"
              variants={containerVariants}
              initial="hidden"
              animate="show"
            >
              {pendingStory && (
                <motion.div 
                  variants={itemVariants}
                  className="flex flex-col items-center justify-center min-h-[60vh]"
                >
                  <h1 className="text-[#343434] text-[2.75rem] md:text-[4.5rem] leading-[110%] tracking-[-0.06375rem] md:tracking-[-0.135rem] mb-4 text-center font-freeman">
                    Klasse! Wir erstellen jetzt deine ganz besondere Hörgeschichte...
                  </h1>
                  <p className="text-[#343434] text-base md:text-[1.25rem] font-normal leading-[150%] mb-8 text-center font-aleo">
                    Gleich ist es soweit – Bitte schließe dieses Fenster nicht, bis die Geschichte fertig ist!
                  </p>
                  <div className="w-[240px] h-[240px]">
                    <DotLottieReact
                      src="https://lottie.host/68ed6426-eac9-43ff-bea8-2c5466e5608a/qVSzwNpApi.lottie"
                      loop
                      autoplay
                      style={{ width: '100%', height: '100%' }}
                    />
                  </div>
                </motion.div>
              )}

              {stories.map((story) => (
                <motion.div
                  key={story.id}
                  variants={itemVariants}
                  layout
                >
                  <StoryThumbnail 
                    story={story} 
                    onClick={() => setSelectedStory(story)}
                  />
                </motion.div>
              ))}
              
              {stories.length === 0 && !pendingStory && (
                <motion.div 
                  variants={itemVariants}
                  className="flex flex-col items-center justify-center min-h-[60vh]"
                >
                  <h1 className="text-[#343434] text-[2.75rem] md:text-[4.5rem] leading-[110%] tracking-[-0.06375rem] md:tracking-[-0.135rem] mb-8 text-center font-freeman">
                    Du hast noch keine Geschichte erstellt.
                  </h1>
                  <a 
                    href="/erstellen" 
                    className="h-14 bg-[#FFA23E] text-[#343434] px-8 py-2 rounded-full font-freeman text-[1.25rem] font-normal leading-[110%] tracking-[-0.0375rem] flex items-center justify-center hover:bg-[#FF9925] transition-colors duration-300"
                  >
                    Erstelle deine erste Geschichte
                  </a>
                </motion.div>
              )}

              {error && (
                <motion.div 
                  variants={itemVariants}
                  className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
                  role="alert"
                >
                  <span className="block sm:inline">{error}</span>
                </motion.div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CreateAccountCard at the bottom */}
      {!user && stories?.length > 0 && (
        <div className="fixed-width-container relative left-[50%] right-[50%] ml-[-50vw] mr-[-50vw] w-screen mt-auto">
          <CreateAccountCard />
        </div>
      )}

      {selectedStory && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 bg-white/5 backdrop-blur-sm flex items-center justify-center p-4 z-[100]"
        >
          <AnimatePresence mode="wait">
            <motion.div 
              key="story-modal"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="relative max-w-2xl w-full flex flex-col items-center"
            >
              <StoryCard story={selectedStory} />
              <motion.button 
                onClick={() => setSelectedStory(null)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-12 h-12 bg-white rounded-full text-[#343434] flex items-center justify-center shadow-lg z-[70] transition-colors mt-4 md:mt-0 md:absolute md:-top-2 md:-right-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </motion.button>
            </motion.div>
          </AnimatePresence>
        </motion.div>
      )}
    </div>
  );
}

export default MyStories; 